import '@/shared/utils/prelude';
import 'mac-scrollbar/dist/mac-scrollbar.css';
import '../styles/globals.css';
import '@rainbow-me/rainbowkit/styles.css';
import { type JSX } from 'react';
import { type AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';
import { DisableSSR } from '@/shared/components/DisableSSR';
import FallbackLayout from '@/shared/components/organisms/FallbackLayout';
import { Provider as RainbowKitWrappedProvider } from '@/shared/components/RainbowKit';
import { isTestnet } from '@/shared/featureFlags';
import { useCanonicalUrl } from '@/shared/hooks/useCanonicalUrl';
import { CoinGeckoPricesProvider } from '@/shared/hooks/useCoinGeckoPrices';
import { EvmScAccountProvider } from '@/shared/hooks/useEvmScAccount';
import { FundingSDKProvider } from '@/shared/hooks/useOldFundingSdk';
import usePlausible from '@/shared/hooks/usePlausible';
import { PolkadotProvider } from '@/shared/hooks/usePolkadot';
import { initSentry } from '@/shared/utils/helpers';
import { defaultRainbowConfig } from '@/shared/utils/rainbowkit';
import { StateChainAccountProvider } from '../hooks/useStateChainAccount';
import useStore from '../hooks/useStore';
import { getPageDescription, getPageTitle } from '../utils/seo';

initSentry();

function App({ Component, pageProps }: AppProps): JSX.Element {
  // Initialize analytics
  usePlausible();

  const canonicalUrl = useCanonicalUrl();
  const { pathname } = useRouter();
  const description = getPageDescription(pathname);
  const title = getPageTitle(pathname);

  const polkadotAccount = useStore((store) => store.polkadotAccount);
  const savePolkadotAccount = useStore((store) => store.savePolkadotAccount);

  return (
    <>
      {/* Rendered outside <DisableSSR> so the static export bakes these tags into the
          raw HTML <head>. Everything inside <DisableSSR> is client-only (ssr: false),
          so a <title> in there would never reach crawlers that don't run JS. */}
      <Head>
        <link rel="icon" href="/chainflip-favicon.ico" sizes="any" />
        <link rel="icon" href="/chainflip-favicon.svg" type="image/svg+xml" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="twitter:description" content={description} />
        <meta property="og:description" content={description} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {isTestnet && <meta name="robots" content="noindex, nofollow" />}
      </Head>
      <DisableSSR>
        <RainbowKitWrappedProvider config={defaultRainbowConfig}>
          <FundingSDKProvider>
            <CoinGeckoPricesProvider>
              <PolkadotProvider
                polkadotAppName="Chainflip Auctions"
                storeAccount={polkadotAccount}
                savePolkadotAccount={savePolkadotAccount}
              >
                <StateChainAccountProvider>
                  <EvmScAccountProvider>
                    <Sentry.ErrorBoundary
                      // eslint-disable-next-line react/no-unstable-nested-components
                      fallback={({ error, resetError }) => {
                        const errorMessage = (error as Error).message;
                        return <FallbackLayout message={errorMessage} onClose={resetError} />;
                      }}
                    >
                      <Component {...pageProps} />
                      <Toaster expand richColors closeButton theme="dark" visibleToasts={10} />
                    </Sentry.ErrorBoundary>
                  </EvmScAccountProvider>
                </StateChainAccountProvider>
              </PolkadotProvider>
            </CoinGeckoPricesProvider>
          </FundingSDKProvider>
        </RainbowKitWrappedProvider>
      </DisableSSR>
    </>
  );
}

export default App;
